/**
 * Team Settings Page
 *
 * Allows workspace owners and admins to manage team members.
 * Shows members list, pending invitations, and role management.
 *
 * ## Features
 * - View all workspace members with their roles
 * - Send invitations to new members (admin+ only)
 * - Change member roles (owner only)
 * - Remove members (admin+ only)
 * - Cancel pending invitations
 *
 * ## Edge Case Operations (Danger Zone)
 * This page also handles critical workspace operations:
 *
 * 1. **Leave Workspace** - Non-owners can voluntarily leave
 *    - User loses access immediately
 *    - No seat cooldown (unlike being removed)
 *    - User is redirected to dashboard
 *
 * 2. **Transfer Ownership** - Owners can transfer to another member
 *    - Current owner becomes admin
 *    - New owner gains full control
 *    - Irreversible without new owner's cooperation
 *
 * 3. **Delete Workspace** - Owners can permanently delete
 *    - Requires typing workspace name to confirm
 *    - Cannot delete default workspace
 *    - Cannot delete only workspace
 *    - All data permanently lost
 *
 * @module pages/team-settings
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UsersRound,
  UserPlus,
  Loader2,
  AlertCircle,
  Trash2,
  Clock,
  CheckCircle,
  Mail,
  Crown,
  Shield,
  User,
  Eye,
  MoreVertical,
  X,
  LogOut,
  ArrowRightLeft,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/client/lib/api';
import { formatDate } from '@/client/lib/utils';
import { useAuth } from '@/client/hooks/use-auth';
import { useWorkspace, type WorkspaceRole } from '@/client/hooks/use-workspace';
import { useNavigate } from 'react-router-dom';
import { SettingsLayout } from '@/client/components/layout/settings-layout';
import { Button } from '@/client/components/ui/button';
import { Input } from '@/client/components/ui/input';
import { Label } from '@/client/components/ui/label';
import { Badge } from '@/client/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/client/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/client/components/ui/dropdown-menu';

interface Member {
  id: string;
  userId: string;
  role: WorkspaceRole;
  joinedAt: string;
  user: {
    id: string;
    name: string;
    email: string;
    image: string | null;
  };
}

interface Invitation {
  id: string;
  email: string;
  role: WorkspaceRole;
  expiresAt: string;
  createdAt: string;
}

interface MembersResponse {
  members: Member[];
  limits: {
    maxMembers: number;
    currentCount: number;
  };
}

interface InvitationsResponse {
  invitations: Invitation[];
  limits: {
    maxPending: number;
    currentCount: number;
  };
}

const ROLE_CONFIG: Record<
  WorkspaceRole,
  { label: string; icon: typeof Crown; color: string; description: string }
> = {
  owner: {
    label: 'Owner',
    icon: Crown,
    color: 'bg-primary/10 text-primary border-primary/20',
    description: 'Full access, can transfer ownership',
  },
  admin: {
    label: 'Admin',
    icon: Shield,
    color: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
    description: 'Full access, can manage members',
  },
  member: {
    label: 'Member',
    icon: User,
    color: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
    description: 'Create and edit links',
  },
  viewer: {
    label: 'Viewer',
    icon: Eye,
    color: 'bg-slate-500/10 text-slate-600 border-slate-500/20',
    description: 'Read-only access',
  },
};

export function TeamSettingsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeWorkspace, activeWorkspaceId, can, isOwner, refetchWorkspaces } = useWorkspace();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member' | 'viewer'>('member');

  // ==========================================================================
  // Edge Case Dialog State
  // ==========================================================================
  // These control the Danger Zone dialogs for critical workspace operations.
  // Each operation has its own dialog with appropriate confirmation UI.

  /** Leave workspace dialog - shown to non-owners */
  const [leaveOpen, setLeaveOpen] = useState(false);

  /** Transfer ownership dialog - shown to owners when workspace has other members */
  const [transferOpen, setTransferOpen] = useState(false);

  /** Delete workspace dialog - shown to owners for non-default workspaces */
  const [deleteOpen, setDeleteOpen] = useState(false);

  /** Selected member ID for ownership transfer (user must choose before confirming) */
  const [selectedTransferMember, setSelectedTransferMember] = useState<string>('');

  /** Confirmation text for delete - must match workspace name exactly */
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Fetch members
  const {
    data: membersData,
    isLoading: membersLoading,
    error: membersError,
  } = useQuery<MembersResponse>({
    queryKey: ['workspace-members', activeWorkspaceId],
    queryFn: async () => {
      const response = await apiFetch(`/api/workspaces/${activeWorkspaceId}/members`);
      if (!response.ok) throw new Error('Failed to fetch members');
      return response.json();
    },
    enabled: !!activeWorkspaceId,
  });

  // Fetch invitations
  const {
    data: invitationsData,
    isLoading: invitationsLoading,
    error: invitationsError,
  } = useQuery<InvitationsResponse>({
    queryKey: ['workspace-invitations', activeWorkspaceId],
    queryFn: async () => {
      const response = await apiFetch(`/api/workspaces/${activeWorkspaceId}/invitations`);
      if (!response.ok) throw new Error('Failed to fetch invitations');
      return response.json();
    },
    enabled: !!activeWorkspaceId,
  });

  // Invite mutation
  const inviteMutation = useMutation({
    mutationFn: async ({ email, role }: { email: string; role: string }) => {
      const response = await apiFetch(`/api/workspaces/${activeWorkspaceId}/members/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, role }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send invitation');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-invitations'] });
      toast.success('Invitation sent successfully');
      setInviteOpen(false);
      setInviteEmail('');
      setInviteRole('member');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Update role mutation
  const updateRoleMutation = useMutation({
    mutationFn: async ({ memberId, role }: { memberId: string; role: string }) => {
      const response = await apiFetch(`/api/workspaces/${activeWorkspaceId}/members/${memberId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update role');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-members'] });
      toast.success('Role updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Remove member mutation
  const removeMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const response = await apiFetch(`/api/workspaces/${activeWorkspaceId}/members/${memberId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to remove member');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-members'] });
      toast.success('Member removed successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Cancel invitation mutation
  const cancelInvitationMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      const response = await apiFetch(
        `/api/workspaces/${activeWorkspaceId}/invitations/${invitationId}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to cancel invitation');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-invitations'] });
      toast.success('Invitation cancelled');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // ==========================================================================
  // Edge Case Mutations
  // ==========================================================================
  // These mutations handle critical workspace operations from the Danger Zone.
  // Each has specific success/error handling and navigation behavior.

  /**
   * Leave Workspace Mutation
   *
   * Allows non-owners to voluntarily leave the workspace.
   * POST /api/workspaces/:workspaceId/leave
   *
   * Behavior:
   * - Membership is deleted immediately
   * - No seat cooldown is created (unlike being removed by admin)
   * - User's activeWorkspaceId is cleared
   * - User is redirected to dashboard
   *
   * Error cases:
   * - Owners cannot leave (must transfer ownership first)
   */
  const leaveWorkspaceMutation = useMutation({
    mutationFn: async () => {
      const response = await apiFetch(`/api/workspaces/${activeWorkspaceId}/leave`, {
        method: 'POST',
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to leave workspace');
      }
      return response.json();
    },
    onSuccess: async () => {
      toast.success('You have left the workspace');
      // Refetch workspaces to update the workspace list in sidebar
      await refetchWorkspaces();
      setLeaveOpen(false);
      // Navigate to dashboard - React Query will auto-select a new workspace
      navigate('/dashboard');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  /**
   * Transfer Ownership Mutation
   *
   * Allows owners to transfer ownership to another workspace member.
   * POST /api/workspaces/:workspaceId/transfer-ownership
   *
   * Behavior:
   * - Current owner is demoted to 'admin' role
   * - Selected member is promoted to 'owner' role
   * - workspace.ownerId is updated
   * - UI refreshes to show new role badges
   *
   * Error cases:
   * - Target must be existing member (not external user)
   * - Only current owner can initiate transfer
   */
  const transferOwnershipMutation = useMutation({
    mutationFn: async (newOwnerId: string) => {
      const response = await apiFetch(`/api/workspaces/${activeWorkspaceId}/transfer-ownership`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newOwnerId }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to transfer ownership');
      }
      return response.json();
    },
    onSuccess: async () => {
      toast.success('Ownership transferred successfully');
      // Refresh members list to show updated roles
      queryClient.invalidateQueries({ queryKey: ['workspace-members'] });
      // Refresh workspaces to update role in sidebar
      await refetchWorkspaces();
      setTransferOpen(false);
      setSelectedTransferMember('');
      // Note: User stays on this page but will see themselves as admin now
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  /**
   * Delete Workspace Mutation
   *
   * Permanently deletes the workspace and ALL associated data.
   * DELETE /api/workspaces/:workspaceId
   *
   * DESTRUCTIVE: This cannot be undone!
   *
   * Behavior:
   * - Workspace and all data (links, folders, etc.) are deleted
   * - All members lose access
   * - All members' activeWorkspaceId is cleared
   * - User is redirected to dashboard
   *
   * Safeguards:
   * - Requires exact workspace name match to confirm
   * - Cannot delete default workspace (isDefault=true)
   * - Cannot delete only workspace (must have another to fall back to)
   */
  const deleteWorkspaceMutation = useMutation({
    mutationFn: async () => {
      const response = await apiFetch(`/api/workspaces/${activeWorkspaceId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete workspace');
      }
      return response.json();
    },
    onSuccess: async () => {
      toast.success('Workspace deleted successfully');
      // Refetch workspaces to remove deleted workspace from list
      await refetchWorkspaces();
      setDeleteOpen(false);
      setDeleteConfirmText('');
      // Navigate to dashboard - React Query will auto-select remaining workspace
      navigate('/dashboard');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleInvite = () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    inviteMutation.mutate({ email: inviteEmail, role: inviteRole });
  };

  const handleRemoveMember = (member: Member) => {
    if (
      window.confirm(
        `Are you sure you want to remove ${member.user.name || member.user.email} from the workspace?`
      )
    ) {
      removeMutation.mutate(member.id);
    }
  };

  const handleCancelInvitation = (invitation: Invitation) => {
    if (window.confirm(`Cancel invitation to ${invitation.email}?`)) {
      cancelInvitationMutation.mutate(invitation.id);
    }
  };

  const isLoading = membersLoading || invitationsLoading;
  const error = membersError || invitationsError;
  const members = membersData?.members || [];
  const invitations = invitationsData?.invitations || [];
  const limits = membersData?.limits;

  const canInvite = can('invite');
  const canManageRoles = can('manage_roles');

  return (
    <SettingsLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="rounded-lg border bg-card p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <UsersRound className="h-5 w-5 text-primary" />
                Team Members
              </h2>
              <p className="text-sm text-muted-foreground">
                Manage who has access to{' '}
                <span className="font-medium">{activeWorkspace?.name || 'this workspace'}</span>
              </p>
            </div>
            {canInvite && (
              <Button onClick={() => setInviteOpen(true)} className="gap-2">
                <UserPlus className="h-4 w-4" />
                Invite Member
              </Button>
            )}
          </div>

          {/* Limits info */}
          {limits && (
            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
              <span>
                {limits.currentCount} / {limits.maxMembers} members
              </span>
              {invitationsData?.limits && (
                <span>
                  {invitationsData.limits.currentCount} / {invitationsData.limits.maxPending}{' '}
                  pending invitations
                </span>
              )}
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-32 text-center">
              <AlertCircle className="h-8 w-8 text-destructive mb-2" />
              <p className="text-sm text-muted-foreground">Failed to load team members</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Members List */}
              <div>
                <h3 className="text-sm font-medium flex items-center gap-2 mb-3">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Active Members ({members.length})
                </h3>
                <div className="divide-y rounded-lg border">
                  {members.map((member) => (
                    <MemberRow
                      key={member.id}
                      member={member}
                      isCurrentUser={member.userId === user?.id}
                      canManageRoles={canManageRoles}
                      canRemove={can('invite') && member.role !== 'owner'}
                      onUpdateRole={(role) =>
                        updateRoleMutation.mutate({ memberId: member.id, role })
                      }
                      onRemove={() => handleRemoveMember(member)}
                      onTransferOwnership={() => {
                        setSelectedTransferMember(member.userId);
                        setTransferOpen(true);
                      }}
                      isOwner={isOwner}
                    />
                  ))}
                </div>
              </div>

              {/* Pending Invitations */}
              {invitations.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium flex items-center gap-2 mb-3">
                    <Clock className="h-4 w-4 text-amber-500" />
                    Pending Invitations ({invitations.length})
                  </h3>
                  <div className="divide-y rounded-lg border border-amber-500/30 bg-amber-500/5">
                    {invitations.map((invitation) => (
                      <InvitationRow
                        key={invitation.id}
                        invitation={invitation}
                        onCancel={() => handleCancelInvitation(invitation)}
                        isCancelling={cancelInvitationMutation.isPending}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Invitations expire after 7 days. Users will receive an email with instructions.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Role descriptions */}
        <div className="rounded-lg border border-dashed p-4">
          <h3 className="font-medium mb-3">Role Permissions</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {(['owner', 'admin', 'member', 'viewer'] as const).map((role) => {
              const config = ROLE_CONFIG[role];
              const Icon = config.icon;
              return (
                <div key={role} className="flex items-start gap-3 p-2">
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${config.color}`}
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{config.label}</p>
                    <p className="text-xs text-muted-foreground">{config.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ================================================================
            Danger Zone Section
            ================================================================
            Contains critical workspace operations that can't be easily undone.
            Each action is conditionally shown based on user's role and context:

            1. Leave Workspace: Visible to non-owners (admins, members, viewers)
               - Owners must transfer ownership first before they can leave

            2. Transfer Ownership: Visible to owners when workspace has 2+ members
               - Owner cannot transfer if they're the only member

            3. Delete Workspace: Visible to owners for non-default workspaces
               - Default workspace (created at signup) cannot be deleted
               - User must have other workspaces to fall back to
            ================================================================ */}
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <h3 className="font-semibold text-destructive">Danger Zone</h3>
          </div>

          <div className="space-y-4">
            {/* Leave Workspace Action
                Shown to: Non-owners only
                Behavior: Opens confirmation dialog, then removes user from workspace */}
            {!isOwner && (
              <div className="flex items-center justify-between p-4 rounded-lg border bg-card">
                <div>
                  <h4 className="font-medium">Leave Workspace</h4>
                  <p className="text-sm text-muted-foreground">
                    Remove yourself from {activeWorkspace?.name || 'this workspace'}
                  </p>
                </div>
                <Button variant="outline" onClick={() => setLeaveOpen(true)} className="gap-2">
                  <LogOut className="h-4 w-4" />
                  Leave
                </Button>
              </div>
            )}

            {/* Transfer Ownership Action
                Shown to: Owners when there are other members to transfer to
                Behavior: Opens dialog with member selector, then demotes owner to admin */}
            {isOwner && members.length > 1 && (
              <div className="flex items-center justify-between p-4 rounded-lg border bg-card">
                <div>
                  <h4 className="font-medium">Transfer Ownership</h4>
                  <p className="text-sm text-muted-foreground">
                    Transfer workspace ownership to another member. You&apos;ll become an admin.
                  </p>
                </div>
                <Button variant="outline" onClick={() => setTransferOpen(true)} className="gap-2">
                  <ArrowRightLeft className="h-4 w-4" />
                  Transfer
                </Button>
              </div>
            )}

            {/* Delete Workspace Action
                Shown to: Owners of non-default workspaces
                Behavior: Opens dialog requiring exact name confirmation, then permanently deletes */}
            {isOwner && !activeWorkspace?.isDefault && (
              <div className="flex items-center justify-between p-4 rounded-lg border border-destructive/30 bg-destructive/5">
                <div>
                  <h4 className="font-medium text-destructive">Delete Workspace</h4>
                  <p className="text-sm text-muted-foreground">
                    Permanently delete this workspace and all its data. This cannot be undone.
                  </p>
                </div>
                <Button variant="destructive" onClick={() => setDeleteOpen(true)} className="gap-2">
                  <Trash2 className="h-4 w-4" />
                  Delete
                </Button>
              </div>
            )}

            {/* Info message for default workspace owners
                Default workspace is protected from deletion */}
            {isOwner && activeWorkspace?.isDefault && (
              <p className="text-sm text-muted-foreground">
                This is your default workspace and cannot be deleted.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation to join {activeWorkspace?.name || 'your workspace'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                type="email"
                placeholder="colleague@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select
                value={inviteRole}
                onValueChange={(value) => setInviteRole(value as 'admin' | 'member' | 'viewer')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {isOwner && (
                    <SelectItem value="admin">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-blue-500" />
                        Admin - Full access, can manage members
                      </div>
                    </SelectItem>
                  )}
                  <SelectItem value="member">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-emerald-500" />
                      Member - Create and edit links
                    </div>
                  </SelectItem>
                  <SelectItem value="viewer">
                    <div className="flex items-center gap-2">
                      <Eye className="h-4 w-4 text-slate-500" />
                      Viewer - Read-only access
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleInvite} disabled={inviteMutation.isPending} className="gap-2">
              {inviteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Mail className="h-4 w-4" />
              )}
              Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================================================================
          Edge Case Confirmation Dialogs
          ================================================================
          Each dangerous action has a dedicated dialog with appropriate
          confirmation UX to prevent accidental operations.
          ================================================================ */}

      {/* Leave Workspace Dialog
          Simple confirmation dialog - no additional input required.
          User just confirms they want to leave. */}
      <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Leave Workspace</DialogTitle>
            <DialogDescription>
              Are you sure you want to leave {activeWorkspace?.name || 'this workspace'}? You will
              lose access to all links and data in this workspace.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLeaveOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => leaveWorkspaceMutation.mutate()}
              disabled={leaveWorkspaceMutation.isPending}
              className="gap-2"
            >
              {leaveWorkspaceMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <LogOut className="h-4 w-4" />
              )}
              Leave Workspace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Ownership Dialog
          Requires user to select a target member from dropdown.
          Button is disabled until a member is selected.
          Members list excludes the current user (can't transfer to yourself). */}
      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transfer Ownership</DialogTitle>
            <DialogDescription>
              Select a member to transfer ownership to. You will become an admin after the transfer.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="new-owner">New Owner</Label>
            <Select value={selectedTransferMember} onValueChange={setSelectedTransferMember}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Select a member" />
              </SelectTrigger>
              <SelectContent>
                {/* Filter out current user - can't transfer ownership to yourself */}
                {members
                  .filter((m) => m.userId !== user?.id)
                  .map((member) => (
                    <SelectItem key={member.userId} value={member.userId}>
                      <div className="flex items-center gap-2">
                        <span>{member.user.name || member.user.email}</span>
                        <Badge variant="outline" className="text-xs">
                          {ROLE_CONFIG[member.role].label}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => transferOwnershipMutation.mutate(selectedTransferMember)}
              disabled={!selectedTransferMember || transferOwnershipMutation.isPending}
              className="gap-2"
            >
              {transferOwnershipMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowRightLeft className="h-4 w-4" />
              )}
              Transfer Ownership
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Workspace Dialog
          DESTRUCTIVE action - requires typing exact workspace name to confirm.
          This pattern prevents accidental deletion of important workspaces.
          Button is disabled until the confirmation text matches exactly. */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete Workspace</DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the workspace{' '}
              <span className="font-semibold">{activeWorkspace?.name}</span> and all of its data.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {/* Confirmation input - must match workspace name exactly */}
            <Label htmlFor="confirm-delete">
              Type <span className="font-mono font-bold">{activeWorkspace?.name}</span> to confirm
            </Label>
            <Input
              id="confirm-delete"
              className="mt-2"
              placeholder="Type workspace name"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteWorkspaceMutation.mutate()}
              disabled={
                // Strict comparison - must match exactly (case-sensitive)
                deleteConfirmText !== activeWorkspace?.name || deleteWorkspaceMutation.isPending
              }
              className="gap-2"
            >
              {deleteWorkspaceMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Delete Workspace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SettingsLayout>
  );
}

function MemberRow({
  member,
  isCurrentUser,
  canManageRoles,
  canRemove,
  onUpdateRole,
  onRemove,
  onTransferOwnership,
  isOwner,
}: {
  member: Member;
  isCurrentUser: boolean;
  canManageRoles: boolean;
  canRemove: boolean;
  onUpdateRole: (role: string) => void;
  onRemove: () => void;
  onTransferOwnership: () => void;
  isOwner: boolean;
}) {
  const config = ROLE_CONFIG[member.role];
  const Icon = config.icon;

  return (
    <div className="flex items-center gap-4 p-4">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center shrink-0">
        <span className="text-sm font-bold text-primary">
          {member.user.name?.charAt(0).toUpperCase() || member.user.email.charAt(0).toUpperCase()}
        </span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium truncate">
            {member.user.name || member.user.email}
            {isCurrentUser && <span className="text-xs text-muted-foreground ml-1">(you)</span>}
          </p>
        </div>
        <p className="text-sm text-muted-foreground truncate">{member.user.email}</p>
        <p className="text-xs text-muted-foreground">Joined {formatDate(member.joinedAt)}</p>
      </div>

      <Badge variant="outline" className={`shrink-0 ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>

      {/* Actions */}
      {!isCurrentUser && member.role !== 'owner' && (canManageRoles || canRemove) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {canManageRoles && (
              <>
                {isOwner && member.role !== 'admin' && (
                  <DropdownMenuItem onClick={() => onUpdateRole('admin')}>
                    <Shield className="h-4 w-4 mr-2 text-blue-500" />
                    Make Admin
                  </DropdownMenuItem>
                )}
                {member.role !== 'member' && (
                  <DropdownMenuItem onClick={() => onUpdateRole('member')}>
                    <User className="h-4 w-4 mr-2 text-emerald-500" />
                    Make Member
                  </DropdownMenuItem>
                )}
                {member.role !== 'viewer' && (
                  <DropdownMenuItem onClick={() => onUpdateRole('viewer')}>
                    <Eye className="h-4 w-4 mr-2 text-slate-500" />
                    Make Viewer
                  </DropdownMenuItem>
                )}
                {isOwner && (
                  <DropdownMenuItem onClick={onTransferOwnership}>
                    <Crown className="h-4 w-4 mr-2 text-amber-500" />
                    Transfer Ownership
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
              </>
            )}
            {canRemove && (
              <DropdownMenuItem onClick={onRemove} className="text-destructive">
                <Trash2 className="h-4 w-4 mr-2" />
                Remove from workspace
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

function InvitationRow({
  invitation,
  onCancel,
  isCancelling,
}: {
  invitation: Invitation;
  onCancel: () => void;
  isCancelling: boolean;
}) {
  const config = ROLE_CONFIG[invitation.role];
  const Icon = config.icon;
  const isExpired = new Date(invitation.expiresAt) < new Date();

  return (
    <div className="flex items-center gap-4 p-4">
      <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center shrink-0">
        <Mail className="h-5 w-5 text-amber-600" />
      </div>

      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{invitation.email}</p>
        <p className="text-xs text-muted-foreground">
          Invited {formatDate(invitation.createdAt)}
          {!isExpired && ` · Expires ${formatDate(invitation.expiresAt)}`}
        </p>
      </div>

      <Badge variant="outline" className={`shrink-0 ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>

      {isExpired && (
        <Badge variant="destructive" className="shrink-0">
          Expired
        </Badge>
      )}

      <Button
        variant="ghost"
        size="icon-sm"
        onClick={onCancel}
        disabled={isCancelling}
        className="hover:bg-destructive/10 hover:text-destructive"
      >
        {isCancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
      </Button>
    </div>
  );
}
